import { Injectable, } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import {
  Installation,
  User,
  fromInstallationGlobalId,
  geoserverGatewayId,
  geoserverGatewayReference,
  mapGeoserverFeature,
  mapInstallationNode,
  mapUserNode,
} from 'src/shared/components/service-order/models/service-order.model';
import { GET_USER_COMPANIES } from 'src/app/graphql/users.graphql';
import { MapjsService } from './mapjs.service';

/**
 * Fonte da hidratação da instalação na Ficha da OS. Teste: 'geoserver' (WFS map_nodes, o mesmo
 * caminho da importação em lote) contra 'graphql' (SERVICE_ORDER_INSTALLATION_LOOKUP). O
 * autocomplete continua no GraphQL: precisa da lista por "contém" e do ID relay para gravar.
 **/
export type InstallationHydrationSource = 'geoserver' | 'graphql';
export const INSTALLATION_HYDRATION_SOURCE: InstallationHydrationSource = 'geoserver';

import {
  CLOSE_SERVICE_ORDER,
  CREATE_SERVICE_ORDER,
  FILTER_SERVICE_ORDER,
  SEARCH_SERVICE_ORDER_USERS,
  SERVICE_ORDER_COMPANY_USERS_PAGE,
  SERVICE_ORDER_INSTALLATION_LOOKUP,
  START_SERVICE_ORDER,
  UPDATE_SERVICE_ORDER,
} from 'src/app/graphql/service-order.queries';

/** Parâmetros aceitos na consulta de ordens de serviço **/
export interface ServiceOrderFilterParams {
  reference?: string | null;
  installationReference?: string | null;
  status?: string | null;
  priority?: string | null;
  /** Global ID do usuário responsável (o bifrost decodifica) **/
  assignee?: string | null;
  offset?: number | null;
  limit?: number | null;
  /** Ordenação no servidor (a paginação é por offset). Sem valor o bifrost usa CREATED_AT DESC **/
  sortBy?: ServiceOrderSortField | null;
  sortOrder?: 'ASC' | 'DESC' | null;
}

export type ServiceOrderSortField = 'CREATED_AT' | 'STARTED_AT' | 'CLOSED_AT' | 'REFERENCE';

/** Campos enviados na criação/atualização de uma ordem de serviço **/
export interface ServiceOrderWriteInput {
  reference?: string | null;
  installationId?: string | null;
  installationReference?: string | null;
  status?: string | null;
  priority?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  deviceMac?: string | null;
  assignee?: string | null;
  problems?: string | null;
  notes?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class ServiceOrderService {
  constructor(
    private apollo: Apollo,
    private mapjsService: MapjsService,
  ) {
    /** Verifica a empresa selecionada **/
    this.company = localStorage.getItem('lastCompanySelected')
      ? localStorage.getItem('lastCompanySelected')
      : null;
  }
  /** Armazena ID da empresa selecionada **/
  company: string | null;

  // Realiza a busca por ordens de serviço na empresa selecionada
  filterServiceOrder(params: ServiceOrderFilterParams = {}): any {
    return this.apollo.watchQuery({
      query: FILTER_SERVICE_ORDER,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      variables: {
        company: this.company,
        // `|| null` nos enums: string vazia quebra a coerção da variável no servidor
        reference: params.reference || null,
        installationReference: params.installationReference || null,
        status: params.status || null,
        priority: params.priority || null,
        assignee: params.assignee || null,
        limit: params.limit ?? null,
        offset: params.offset ?? null,
        sortBy: params.sortBy || null,
        sortOrder: params.sortOrder || null,
      },
    });
  }

  // Método de busca das instalações para preencher nos campos de cadastro
  /**
   * Busca de instalação para a OS pelo que o operador tem em mãos: parte da referência da
   * instalação ou o serial do equipamento (etiqueta em campo). O id interno (base64) nunca é
   * digitado. Duas consultas em paralelo — `referenceContains` (parcial) e `serialNumber`
   * (exato, `device__serial_number` no bifrost) — mescladas sem duplicata, serial primeiro
   * porque é o casamento mais forte.
   **/
  searchInstallations(term: string | null | undefined): Observable<Installation[]> {
    const search = term?.trim();

    if (!search) {
      return of([]);
    }

    return forkJoin([
      this.queryInstallations({ serialNumber: search }, 5),
      this.queryInstallations({ referenceContains: search }, 20),
    ]).pipe(
      map(([bySerial, byReference]) => {
        const seen = new Set<string>();

        return [...bySerial, ...byReference].filter(installation => {
          if (!installation.id || seen.has(installation.id)) {
            return false;
          }

          seen.add(installation.id);

          return true;
        });
      }),
    );
  }

  /**
   * Serial completo do equipamento -> instalação (a primeira, se houver mais de uma). Usado pela
   * barra da tabela: a consulta de OS só filtra por referência da instalação, então o serial é
   * traduzido antes.
   **/
  findInstallationBySerial(serial: string | null | undefined): Observable<Installation | null> {
    const search = serial?.trim();

    if (!search) {
      return of(null);
    }

    return this.queryInstallations({ serialNumber: search }, 1).pipe(map(installations => installations[0] ?? null));
  }

  /**
   * Hidrata a instalação de uma OS já aberta: pelo ID (a plaqueta pode ter sido renomeada
   * depois da OS, o ID não), com a referência como fallback para OS antiga sem id.
   **/
  hydrateInstallation(installation: Installation | null | undefined): Observable<Installation | null> {
    const id = installation?.id || null;
    const reference = installation?.reference || null;

    if (!id && !reference) {
      return of(null);
    }

    if (INSTALLATION_HYDRATION_SOURCE === 'geoserver') {
      return this.findInstallationOnGeoserver(id, reference);
    }

    return id ? this.findInstallationById(id) : this.findInstallationByReference(reference);
  }

  /**
   * WFS da camada map_nodes (GeoServer), como a importação em lote. CQL pelo `id` numérico
   * (decodificado do ID relay) ou, sem id, pela referência em minúsculas. O `&sortBy=reference`
   * é parâmetro WFS separado: map_nodes (dev) não tem PK e o GeoServer exige ordenação
   * explícita para avaliar IN. Erro de rede vira "não encontrada" — o painel é contexto.
   *
   * Gateway: map_nodes só traz `gateway_id`, então vai uma segunda consulta na camada `gateways`
   * pela referência. Falha nessa segunda não derruba a instalação: gateway fica "não informado".
   **/
  findInstallationOnGeoserver(globalId: string | null, reference: string | null): Observable<Installation | null> {
    const numericId = fromInstallationGlobalId(globalId);
    const company = this.company ? atob(this.company) : null;

    if (!company || (numericId === null && !reference)) {
      return of(null);
    }

    const cql = numericId !== null
      ? `"id" = ${numericId}`
      : `strToLowerCase(reference) IN ('${reference!.trim().toLowerCase().replace(/'/g, "''")}')&sortBy=reference`;

    return this.mapjsService.filterByElements(cql, company).pipe(
      map((response: any) => {
        const features: any[] = response?.features ?? [];
        // Sem filtro is_active a mesma referência pode voltar ativa e inativa: prefere a ativa
        const preferred = features.find(feature => {
          const active = feature?.properties?.is_active;

          return active === true || String(active).toLowerCase() === 'true';
        }) ?? features[0];

        return { installation: mapGeoserverFeature(preferred), gatewayId: geoserverGatewayId(preferred) };
      }),
      switchMap(({ installation, gatewayId }) => {
        if (!installation || gatewayId === null || installation.details?.gatewayReference) {
          return of(installation);
        }

        return this.gatewayReference(company, gatewayId).pipe(
          map((reference) => {
            if (installation.details) {
              installation.details.gatewayReference = reference;
            }

            return installation;
          }),
        );
      }),
      catchError(() => of(null)),
    );
  }

  /** Referência de gateway já consultada nesta sessão, por empresa e id: várias OS costumam apontar pro mesmo **/
  private readonly gatewayReferenceCache = new Map<string, Observable<string | null>>();

  /**
   * Segunda consulta ao GeoServer (camada gateways), com cache em memória. Erro não entra no
   * cache: a próxima Ficha tenta de novo. Gateway renomeado só aparece depois de recarregar a página.
   **/
  private gatewayReference(company: string, gatewayId: number): Observable<string | null> {
    const key = `${company}:${gatewayId}`;
    const cached = this.gatewayReferenceCache.get(key);

    if (cached) {
      return cached;
    }

    const request$ = this.mapjsService.getGatewayById(company, gatewayId).pipe(
      map((response: any) => geoserverGatewayReference(response)),
      catchError(() => {
        this.gatewayReferenceCache.delete(key);

        return of(null);
      }),
      shareReplay(1),
    );

    this.gatewayReferenceCache.set(key, request$);

    return request$;
  }

  /** Instalação de uma OS já aberta, pelo ID relay (GraphQL) **/
  findInstallationById(globalId: string | null | undefined): Observable<Installation | null> {
    if (!globalId) {
      return of(null);
    }

    return this.queryInstallations({ installations: [globalId] }, 1).pipe(map(installations => installations[0] ?? null));
  }

  /**
   * Fallback pela referência (GraphQL, OS antiga sem installationId). A consulta de instalação
   * não tem igualdade por referência, então busca por "contém" e fica com a igual.
   **/
  findInstallationByReference(reference: string | null | undefined): Observable<Installation | null> {
    const search = reference?.trim();

    if (!search) {
      return of(null);
    }

    return this.queryInstallations({ referenceContains: search }, 20).pipe(
      map(installations => installations.find(installation => installation.reference?.trim().toLowerCase() === search.toLowerCase()) ?? null),
    );
  }

  /** Documento enxuto (SERVICE_ORDER_INSTALLATION_LOOKUP): só o que o painel e o formulário usam **/
  private queryInstallations(
    filter: { referenceContains?: string; serialNumber?: string; installations?: string[] },
    pageSize: number,
  ): Observable<Installation[]> {
    return this.apollo
      .query<any>({
        query: SERVICE_ORDER_INSTALLATION_LOOKUP,
        fetchPolicy: 'network-only',
        variables: {
          company: this.company,
          ...filter,
          pageSize,
        },
      })
      .pipe(map(response => (response?.data?.installation?.edges ?? []).map((edge: any) => mapInstallationNode(edge?.node))));
  }

  createServiceOrder(input: ServiceOrderWriteInput) {
    return this.apollo.mutate({
      mutation: CREATE_SERVICE_ORDER,
      variables: {
        company: this.company,
        reference: input.reference ?? null,
        installationId: input.installationId ?? null,
        installationReference: input.installationReference ?? null,
        status: input.status ?? null,
        priority: input.priority ?? null,
        latitude: input.latitude ?? null,
        longitude: input.longitude ?? null,
        deviceMac: input.deviceMac ?? null,
        assignee: input.assignee ?? null,
        problems: input.problems ?? null,
        notes: input.notes ?? null,
      },
    });
  }

  // Função criada para atualização de OS
  updateServiceOrder(id: string, input: ServiceOrderWriteInput) {
    return this.apollo.mutate({
      mutation: UPDATE_SERVICE_ORDER,
      variables: {
        id,
        company: this.company,
        ...input,
      },
    });
  }

  /**
   * Mitigação de bug do alerts-api: start_service_order/close_service_order gravam apenas
   * started_at/closed_at/close_notes e nunca a coluna status. O único caminho que grava status
   * é o serviceOrderUpdate, então ele é chamado logo depois da transição (mesma estratégia
   * usada pelo app mobile).
   **/
  // Função criada para vincular um alerta a uma OS
  linkAlertToOS(
    id: string,
    alertId: string
  ) {
    return this.apollo.mutate({
      mutation: UPDATE_SERVICE_ORDER,
      variables: {
        id,
        company: this.company,
        chAlertId: alertId
      },
    });
  }

  /** Autocomplete de técnico: login, nome ou sobrenome num termo só (argumento `search` do bifrost) **/
  public searchUsers(term: string | null | undefined): Observable<User[]> {
    return this.queryUsers(SEARCH_SERVICE_ORDER_USERS, { search: term?.trim() || null });
  }

  /**
   * Todos os usuários vinculados à empresa (importação em lote). O graphene limita cada página a
   * 100, então segue `pageInfo.endCursor` até `hasNextPage` ser falso; pedir só a primeira página
   * deixava de fora quem passava do centésimo.
   */
  public getAllCompanyUsers(company: string | null): Observable<User[]> {
    const pageSize = 100;
    const fetchPage = (after: string | null, acc: User[]): Observable<User[]> =>
      this.apollo.query<any>({
        query: SERVICE_ORDER_COMPANY_USERS_PAGE,
        fetchPolicy: 'network-only',
        variables: { company, first: pageSize, after },
      }).pipe(
        switchMap(response => {
          const connection = response?.data?.user;
          const users = [...acc, ...(connection?.edges ?? []).map((edge: any) => mapUserNode(edge?.node))];
          const pageInfo = connection?.pageInfo;
          return pageInfo?.hasNextPage && pageInfo?.endCursor ? fetchPage(pageInfo.endCursor, users) : of(users);
        }),
      );
    return fetchPage(null, []);
  }

  /** Mantido para a tela de Alertas (alert-edit), que ainda consome `valueChanges` **/
  public getUsers(username: string) {
    return this.apollo.watchQuery({
      query: GET_USER_COMPANIES,
      variables: {
        username_Icontains: username ? username : null,
      },
    });
  }

  private queryUsers(query: any, variables: Record<string, string | null>): Observable<User[]> {
    return this.apollo
      .query<any>({ query, fetchPolicy: 'network-only', variables: { ...variables, first: 20 } })
      .pipe(map(response => (response?.data?.user?.edges ?? []).map((edge: any) => mapUserNode(edge?.node))));
  }

  // Inicia a ordem de serviço
  public startServiceOrder(id: string) {
    return this.apollo.mutate({
      mutation: START_SERVICE_ORDER,
      variables: {
        id
      }
    })
  }

  // Fecha a ordem de serviço
  /** Fecha a OS: o alerts-api grava closed_at, close_notes e o status (CLOSED ou CLOSED_WITH_ISSUES) **/
  public closeServiceOrder(id: string, closeNotes: string, withIssues: boolean = false) {
    return this.apollo.mutate({
      mutation: CLOSE_SERVICE_ORDER,
      variables: {
        id,
        closeNotes,
        withIssues,
      },
    });
  }
}
